// defaultTemplate.ts
import { categoryOptions } from './categories';
import { CategoryTemplate } from '../../types';
import { templateApi, authApi } from '../api/authApi';
import { getSupabase } from '../api/supabase';

// DEBUG: Manual auth state inspector
export const debugAuthState = async () => {
  console.log('🔍 MANUAL AUTH DEBUG START');
  
  try {
    // Check Supabase session directly
    const supabase = getSupabase();
    const { data: { session }, error } = await supabase.auth.getSession();
    
    console.log('🔍 Direct Supabase session check:', {
      hasSession: !!session,
      hasUser: !!session?.user,
      hasAccessToken: !!session?.access_token,
      userEmail: session?.user?.email,
      tokenPrefix: session?.access_token?.substring(0, 20) + '...',
      error: error?.message
    });
    
    // Check localStorage
    const allKeys = Object.keys(localStorage);
    const supabaseKeys = allKeys.filter(key => 
      key.includes('sb') || key.includes('supabase')
    );
    console.log('🔍 localStorage Supabase keys:', supabaseKeys);
    
    // Test API call manually
    if (session?.access_token) {
      console.log('🧪 Testing manual API call with token...');
      const response = await fetch('/api/templates', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      console.log('🧪 Manual API test result:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log('🧪 Manual API error details:', errorData);
      }
    }
    
  } catch (error) {
    console.error('❌ Auth debug failed:', error);
  }
  
  console.log('🔍 MANUAL AUTH DEBUG END');
};

// Expose debug function globally for manual testing
if (typeof window !== 'undefined') {
  (window as any).debugAuth = debugAuthState;
}

export const createDefaultTemplate = async (userId: string): Promise<CategoryTemplate | null> => {
  try {
    // Small delay to ensure auth token is fully established
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Validate session first
    const sessionValidation = await authApi.validateSession();
    if (!sessionValidation.valid) {
      console.error('Invalid session for creating default template');
      return null;
    }
    
    // Create the default template with detailed debugging
    const defaultTemplate = {
      name: 'System Default Template',
      categories: categoryOptions,
      is_default: true
    };

    console.log('📝 Creating default template for user:', userId);
    console.log('📊 Template data size:', JSON.stringify(defaultTemplate).length, 'characters');
    console.log('📋 Categories count:', categoryOptions.length);
    console.log('🔍 Template structure:', {
      name: defaultTemplate.name,
      categoriesCount: defaultTemplate.categories.length,
      isDefault: defaultTemplate.is_default,
      firstCategory: defaultTemplate.categories[0]?.value || 'none',
      userId: userId
    });

    const data = await templateApi.create(defaultTemplate);
    console.log('✅ Default template created successfully:', data.id);

    return data;
  } catch (error) {
    console.error('❌ Error in createDefaultTemplate:', error);
    
    // Enhanced error reporting
    if (error?.message?.includes('Request failed')) {
      console.error('🚨 Template API server error - check backend logs');
      console.error('📡 Possible causes:');
      console.error('  - Database schema mismatch');
      console.error('  - Supabase RLS policies blocking insert');
      console.error('  - Data size exceeding limits');
      console.error('  - Missing user permissions');
      console.error('  - Network connectivity issues');
    }
    
    return null;
  }
};

// Test function to create a minimal template for debugging
export const createMinimalTestTemplate = async (userId: string): Promise<CategoryTemplate | null> => {
  try {
    console.log('🧪 Testing minimal template creation...');
    
    const minimalTemplate = {
      name: 'Test Template',
      categories: [
        {
          value: 'Cash',
          label: 'Cash',
          type: 'current-asset',
          fsType: 'balance-sheet',
          tagsInclude: ['cash'],
          tagsExclude: [],
          isNonCash: false,
          isPpe: false,
          isDebt: false,
          isEquityCapital: false
        }
      ],
      is_default: false
    };

    console.log('🧪 Creating minimal test template...');
    const data = await templateApi.create(minimalTemplate);
    console.log('✅ Minimal template created successfully:', data.id);
    
    // Clean up test template
    try {
      await templateApi.delete(data.id);
      console.log('🧹 Test template cleaned up');
    } catch (cleanupError) {
      console.warn('⚠️ Could not clean up test template:', cleanupError);
    }
    
    return data;
  } catch (error) {
    console.error('❌ Minimal template test failed:', error);
    return null;
  }
};

// Prevent race conditions with multiple simultaneous calls
const templateCreationPromises = new Map<string, Promise<void>>();

export const ensureDefaultTemplateExists = async (userId: string): Promise<void> => {
  // Check if we're already in the process of creating a template for this user
  if (templateCreationPromises.has(userId)) {
    console.log('⏳ Template creation already in progress for user - waiting...:', userId);
    return templateCreationPromises.get(userId);
  }

  const creationPromise = async (): Promise<void> => {
    try {
      console.log('Checking for default template for user:', userId);
      
      // First check if we can even access templates API
      let templates;
      try {
        templates = await templateApi.getAll();
      } catch (apiError) {
        console.error('❌ Cannot access templates API:', apiError);
        // Fail gracefully - don't block app initialization
        return;
      }
      
      // Check if user already has a default template
      const existingTemplate = templates.find(t => t.is_default);

      // If no default template exists, create one
      if (!existingTemplate) {
        console.log('No default template found, creating one...');
        
        const newTemplate = await createDefaultTemplate(userId);
        if (!newTemplate) {
          console.warn('⚠️ Failed to create default template, but continuing...');
        }
      } else {
        console.log('Default template exists:', existingTemplate.id);
      }
    } catch (error) {
      console.error('❌ Error in ensureDefaultTemplateExists:', error);
      // Don't throw - let the app continue without templates if needed
    } finally {
      // Clean up the promise cache
      templateCreationPromises.delete(userId);
    }
  };

  const promise = creationPromise();
  templateCreationPromises.set(userId, promise);
  return promise;
};
